import { useEffect, useState, useSyncExternalStore } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { LayoutShell } from '../../components/LayoutShell';
import { SecondaryPane } from '../../components/SecondaryPane';
import { CalendarDays, ListTodo, ClipboardCheck } from '../../icons';
import { getManagerScope } from '../../api/reservation';
import { tRsv, getRsvLang, subscribeRsvLang } from '../../lib/reservation-i18n';
import { ReservationAsideContext } from './ReservationAside';
import './ReservationPage.css';

/**
 * 예약 메뉴 셸 (2026-08-12) — ② 좌측 상세에 하위 메뉴 3개, ③ 컨텐츠에 각 화면.
 *
 * 레지스트리(`admin_menu_registry`)에는 부모/자식 개념이 없어 하위 메뉴를 행으로 올릴 수 없다
 * (올리면 좌측 레일에 형제로 나란히 뜬다). 프로젝트 메뉴와 같은 방식으로 **부모 1행 +
 * 평면 라우트 + 여기 서브 네비**로 구성한다.
 *
 * "승인처리" 는 담당자로 지정된 사용자에게만 보인다 — 사용자 단위 메뉴 권한 테이블이 없어
 * 서버(`/reservation/me/manager-scope`)가 담당 여부를 내려준다. 이 숨김은 UX 용이고,
 * 실제 차단은 서버 핸들러(`canDecide`)가 한다.
 */
export function ReservationPage() {
  useSyncExternalStore(subscribeRsvLang, getRsvLang, getRsvLang);
  const { pathname } = useLocation();
  const [managerScope, setManagerScope] = useState(0);
  // ② 하단 자리 — 자식 화면(자원 목록·필터)이 포털로 채운다(ReservationAside).
  const [asideHost, setAsideHost] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    // 실패는 조용히 0 으로 — 승인처리 메뉴만 안 보일 뿐 나머지 화면은 정상 동작한다.
    void getManagerScope()
      .then((n) => { if (!cancelled) setManagerScope(n); })
      .catch(() => { if (!cancelled) setManagerScope(0); });
    return () => { cancelled = true; };
  }, [pathname]);

  const items = [
    { to: '/reservation/book', label: tRsv('rsv.nav.book'), Icon: CalendarDays },
    { to: '/reservation/mine', label: tRsv('rsv.nav.mine'), Icon: ListTodo },
    ...(managerScope > 0
      ? [{ to: '/reservation/approvals', label: tRsv('rsv.nav.approvals'), Icon: ClipboardCheck }]
      : []),
  ];

  return (
    <LayoutShell
      secondary={(
        <SecondaryPane title={tRsv('rsv.title')}>
          <ul className="rsv-nav">
            {items.map(({ to, label, Icon }) => (
              <li key={to}>
                <NavLink
                  to={to}
                  className={({ isActive }) => `rsv-nav__item${isActive ? ' is-active' : ''}`}
                >
                  <Icon size={16} aria-hidden />
                  <span>{label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
          <div className="rsv-aside" ref={setAsideHost} />
        </SecondaryPane>
      )}
    >
      <ReservationAsideContext.Provider value={asideHost}>
        <Outlet />
      </ReservationAsideContext.Provider>
    </LayoutShell>
  );
}
